import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Brand, Button } from '../../ui';
import { spring, prefersReducedMotion } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { ltr } from '../../lib/format';
import { useStrings } from '../../state/useLocale';
import type { Strings } from '../../lib/strings';
import type { HudProps } from '../contracts';
import type { GitState } from '../../types';
import { usesGit } from '../../lib/uses-git';

/**
 * The top bar: which level you are on, what the repository looks like right
 * now, and the two things you can do.
 *
 * The two status chips are the learner's peripheral vision, so when one
 * changes because a command landed it flashes. Everything that does not
 * change while you work was taken out.
 */

type Tone = 'plain' | 'on' | 'alert' | 'off';

type HudStrings = Strings['play']['hud'];

function branchChip(git: GitState, s: HudStrings): { text: string; tone: Tone } {
  if (!git.isRepo) return { text: s.noRepo, tone: 'off' };
  if (git.head.detached) return { text: s.detachedAt(ltr(git.head.hash?.slice(0, 7) ?? '?')), tone: 'alert' };
  // A branch name is the machine's, not a word: `main` stays `main`, and it
  // is isolated so bidi cannot reorder a name like `feat/cart-2` in the bar.
  return { text: git.head.branch ? ltr(git.head.branch) : s.unbornBranch, tone: 'on' };
}

/**
 * The working-tree chip.
 *
 * The phrase used to be assembled here out of `${n} staged` fragments joined
 * with ` · `, which decides the order and the separator on every language's
 * behalf and hands a translator three halves of a sentence. The counts go to
 * the catalogue now and the finished phrase comes back, so Arabic can agree
 * each noun with its own number and order them its own way.
 */
function treeChip(git: GitState, s: HudStrings): { text: string; tone: Tone } {
  if (!git.isRepo) return { text: s.noRepo, tone: 'off' };
  const st = git.status;
  if (st.conflicted.length) return { text: s.conflicted(st.conflicted.length), tone: 'alert' };
  const changes = st.staged.length + st.unstaged.length + st.untracked.length;
  if (changes === 0) return { text: s.clean, tone: 'on' };
  return { text: s.treeStatus(st.staged.length, st.unstaged.length, st.untracked.length), tone: 'plain' };
}

const toneClass = (tone: Tone) =>
  `tag${tone === 'on' ? ' tag-on' : tone === 'alert' ? ' tag-alert' : tone === 'off' ? ' tag-off' : ''}`;

/** A chip that pulses when its text changes, keyed off a counter so the
 *  first render never fires. */
function LiveChip({ text, tone, chime }: { text: string; tone: Tone; chime?: boolean }) {
  const [pulse, setPulse] = useState(0);
  const prev = useRef<string | null>(null);

  useEffect(() => {
    if (prev.current !== null && prev.current !== text) {
      setPulse((n) => n + 1);
      if (chime) sound.click();
    }
    prev.current = text;
  }, [text, chime]);

  if (prefersReducedMotion()) return <span className={toneClass(tone)}>{text}</span>;

  return (
    <motion.span
      key={pulse}
      className={toneClass(tone)}
      initial={pulse === 0 ? false : { scale: 1.07 }}
      animate={{ scale: 1 }}
      transition={spring}
    >
      {text}
    </motion.span>
  );
}

export function Hud({ mission, git, checking, sandbox = false, onCheck, checkBlocked, onReset, onWarmup }: HudProps) {
  const strings = useStrings();
  const s = strings.play;
  /*
   * The chapter's name, drawn rather than joined.
   *
   * `mission.world` is the English join key — it ties a level to its opener
   * and to the section on the map, and it stays English in the data for that
   * reason (docs/i18n.md §6.1). This is the one place it is *shown*, so this
   * is where it becomes Arabic. The same idiom as Debrief and Opener.
   */
  const worldName = strings.home.worlds[mission.world]?.name ?? mission.world;
  const branch = branchChip(git, s.hud);
  const tree = treeChip(git, s.hud);

  return (
    <>
      <Brand compact title={s.hud.brandTitle} />

      <div className="hud-id">
        <span className="hud-num">{String(mission.level).padStart(2, '0')}</span>
        <span className="hud-names">
          <span className="hud-title">{mission.title}</span>
          <span className="hud-world label">{sandbox ? s.hud.sandboxWorld(worldName) : worldName}</span>
        </span>
      </div>

      {/* With no repository both chips would say the same thing, so say it once. */}
      <div className="hud-state">
        {git.isRepo ? (
          <>
            <LiveChip text={branch.text} tone={branch.tone} chime />
            <LiveChip text={tree.text} tone={tree.tone} />
          </>
        ) : (
          usesGit(mission) && <LiveChip text={s.hud.noRepoYet} tone="off" />
        )}
      </div>

      <div className="hud-actions">
        {/* The warm-up opened itself once, before the level did. This is the
            way back to it, and it is quiet on purpose: it is worth finding
            halfway through a level, never worth interrupting one. */}
        {mission.tutorial && onWarmup && (
          <Button variant="quiet" onClick={onWarmup} title={s.hud.warmupTitle}>
            {s.hud.warmup}
          </Button>
        )}
        <Button variant="quiet" onClick={onReset} title={s.hud.resetTitle}>
          {s.hud.reset}
        </Button>
        {/* Absent in a sandbox rather than disabled. A greyed-out Check
            would say "you are not allowed to be graded yet", when the truth
            is that there is nothing here to grade — no objectives, no pass,
            no record. A button that cannot do anything is worse than no
            button, because the learner has to work out which it is. */}
        {!sandbox && (
          <Button variant="solid" onClick={onCheck} disabled={checking || checkBlocked !== undefined} title={checkBlocked ?? s.hud.checkTitle}>
            {checking ? s.hud.checking : s.hud.check}
          </Button>
        )}
      </div>
    </>
  );
}

export default Hud;
